import React from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Trash2,
  Flag,
  AlertTriangle,
  Ban,
  ShieldCheck,
  Loader2,
  RefreshCw,
  MapPin,
  Eye,
  EyeOff,
  Share2,
  Gift,
  Save,
  Check,
  Users,
  Download,
  Bell,
  History,
  Clock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABEL = {
  normal: "Normal",
  blue_badge: "Blue Badge only",
  loading_bay: "Loading bay",
  motorcycle: "Motorcycle",
};

const truncDevice = (d) => (d ? `${d.slice(0, 8)}…${d.slice(-6)}` : "—");
const MALTA_TZ = "Europe/Malta";
const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: MALTA_TZ }); }
  catch { return iso; }
};

/* ===== Spots tab ===== */
export function SpotsTab({ adminKey }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [includeHidden, setIncludeHidden] = React.useState(true);
  const [includeExpired, setIncludeExpired] = React.useState(false);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/spots`, {
        headers: { "X-Admin-Key": adminKey },
        params: { include_hidden: includeHidden, include_expired: includeExpired },
      });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load spots.");
    } finally { setLoading(false); }
  }, [adminKey, includeHidden, includeExpired]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const remove = async (id) => {
    if (!window.confirm("Force-remove this spot?")) return;
    try {
      await axios.delete(`${API}/admin/spots/${id}`, { headers: { "X-Admin-Key": adminKey } });
      toast.success("Spot removed.");
      setItems((s) => s.filter((x) => x.id !== id));
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove."); }
  };

  return (
    <div data-testid="admin-spots-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="inline-flex items-center gap-2 text-xs text-zinc-400">
          <input type="checkbox" checked={includeHidden} onChange={(e) => setIncludeHidden(e.target.checked)} />
          Include hidden
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-zinc-400">
          <input type="checkbox" checked={includeExpired} onChange={(e) => setIncludeExpired(e.target.checked)} />
          Include expired
        </label>
        <Button onClick={fetchAll} size="sm" variant="ghost" className="ml-auto h-8 text-zinc-300">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <p className="text-[11px] text-zinc-600 -mt-3 mb-4">
        Live collection only — expired drops are kept here for 24 h, then purged. Full
        lifecycle statistics live in the <span className="text-zinc-400">History</span> tab.
        All times shown in Malta time.
      </p>

      <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500">No spots match filters.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((s) => (
              <div key={s.id} className="p-4 flex items-start gap-3" data-testid={`admin-spot-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-white text-sm">{TYPE_LABEL[s.parking_type] || s.parking_type}</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{s.duration_min} min</span>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        (s.source || "leaving") === "spotted"
                          ? "bg-[#22d3ee]/10 text-[#67e8f9]"
                          : "bg-[#ccff00]/10 text-[#ccff00]"
                      }`}
                      data-testid={`admin-spot-source-${s.id}`}
                    >
                      {(s.source || "leaving") === "spotted" ? "spotted" : "leaving"}
                    </span>
                    {s.hidden && <span className="text-[10px] uppercase tracking-wider text-amber-400 inline-flex items-center gap-1"><EyeOff className="w-3 h-3"/> hidden</span>}
                    {s.report_count > 0 && <span className="text-[10px] uppercase tracking-wider text-red-400 inline-flex items-center gap-1"><Flag className="w-3 h-3"/> {s.report_count}</span>}
                  </div>
                  <div className="mt-1 text-[11px] text-zinc-500 flex flex-wrap gap-x-3">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/> {s.lat.toFixed(5)}, {s.lng.toFixed(5)}</span>
                    <span>created {fmt(s.created_at)}</span>
                    <span>expires {fmt(s.expires_at)}</span>
                  </div>
                  <div className="mt-1 text-[10px] text-zinc-600 font-mono">device {truncDevice(s.device_id)}</div>
                </div>
                <button
                  onClick={() => remove(s.id)}
                  className="shrink-0 h-9 px-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-red-500/20"
                  data-testid={`admin-remove-spot-${s.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== Reports tab ===== */
export function ReportsTab({ adminKey }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports`, { headers: { "X-Admin-Key": adminKey } });
      setItems(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load reports."); }
    finally { setLoading(false); }
  }, [adminKey]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div data-testid="admin-reports-tab">
      <div className="flex items-center mb-5">
        <div className="text-xs text-zinc-500">{items.length} total reports</div>
        <Button onClick={fetchAll} size="sm" variant="ghost" className="ml-auto h-8 text-zinc-300">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500">No reports yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
                <span className="inline-flex items-center gap-1.5 text-amber-300"><Flag className="w-3.5 h-3.5"/> spot</span>
                <span className="text-zinc-300 font-mono">{r.spot_id.slice(0,8)}…</span>
                <span className="text-zinc-500">by device</span>
                <span className="text-zinc-300 font-mono">{truncDevice(r.device_id)}</span>
                <span className="ml-auto text-zinc-500">{fmt(r.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== Devices tab ===== */
export function DevicesTab({ adminKey }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/devices`, { headers: { "X-Admin-Key": adminKey } });
      setItems(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load devices."); }
    finally { setLoading(false); }
  }, [adminKey]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleBan = async (d) => {
    const action = d.banned ? "unban" : "ban";
    if (!window.confirm(`${action} this device?`)) return;
    try {
      await axios.post(`${API}/admin/devices/${action}`, { device_id: d.device_id, reason: action === "ban" ? "admin" : null }, {
        headers: { "X-Admin-Key": adminKey },
      });
      toast.success(`Device ${action}ned.`);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed."); }
  };

  return (
    <div data-testid="admin-devices-tab">
      <div className="flex items-center mb-5">
        <div className="text-xs text-zinc-500">{items.length} devices have dropped spots</div>
        <Button onClick={fetchAll} size="sm" variant="ghost" className="ml-auto h-8 text-zinc-300">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500">No devices yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((d) => (
              <div key={d.device_id} className="p-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]" data-testid={`admin-device-${d.device_id}`}>
                <span className="text-zinc-300 font-mono">{truncDevice(d.device_id)}</span>
                <span className="text-zinc-500">spots: <span className="text-zinc-300">{d.spots_count}</span></span>
                <span className="text-zinc-500">last: <span className="text-zinc-300">{fmt(d.last_seen)}</span></span>
                {d.banned && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-300"><Ban className="w-3 h-3"/> banned</span>}
                <button
                  onClick={() => toggleBan(d)}
                  className={`ml-auto h-8 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 ${
                    d.banned
                      ? "bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] hover:bg-[#ccff00]/20"
                      : "bg-red-500/10 border border-red-500/30 text-red-200 hover:bg-red-500/20"
                  }`}
                >
                  {d.banned ? <><ShieldCheck className="w-3.5 h-3.5" /> Unban</> : <><Ban className="w-3.5 h-3.5" /> Ban</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ===== Referrals tab ===== */
export function ReferralsTab({ adminKey }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referrals`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setItems(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Session expired.");
      else toast.error("Failed to load referrals.");
    } finally {
      setLoading(false);
    }
  }, [adminKey]);
  React.useEffect(() => { load(); }, [load]);

  const totals = React.useMemo(() => {
    return items.reduce(
      (acc, it) => ({
        codes: acc.codes + 1,
        visits: acc.visits + (it.visits || 0),
        referred: acc.referred + (it.unique_referred || 0),
        qualified: acc.qualified + (it.qualified_count || 0),
      }),
      { codes: 0, visits: 0, referred: 0, qualified: 0 }
    );
  }, [items]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Codes" value={totals.codes} icon={<Share2 className="w-3.5 h-3.5" />} />
        <StatCard label="Visits" value={totals.visits} icon={<Eye className="w-3.5 h-3.5" />} />
        <StatCard label="Referred" value={totals.referred} icon={<MapPin className="w-3.5 h-3.5" />} />
        <StatCard label="Qualified" value={totals.qualified} icon={<Check className="w-3.5 h-3.5" />} />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">Total codes: <span className="text-white">{items.length}</span></div>
        <Button onClick={load} variant="ghost" size="sm" className="h-8 text-zinc-300 hover:bg-white/5" data-testid="referrals-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline ml-2">Refresh</span>
        </Button>
      </div>

      <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-zinc-500" data-testid="referrals-empty">
            No referral codes yet.
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.18em] text-zinc-500 border-b border-white/5">
                    <th className="py-3 px-5 font-semibold">Code</th>
                    <th className="py-3 px-5 font-semibold">Referrer</th>
                    <th className="py-3 px-5 font-semibold text-right">Visits</th>
                    <th className="py-3 px-5 font-semibold text-right">Referred</th>
                    <th className="py-3 px-5 font-semibold text-right">Qualified</th>
                    <th className="py-3 px-5 font-semibold">First</th>
                    <th className="py-3 px-5 font-semibold">Last</th>
                    <th className="py-3 px-5 font-semibold">Flags</th>
                  </tr>
                </thead>
                <tbody data-testid="referrals-table">
                  {items.map((r) => (
                    <tr key={r.code} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-3 px-5 font-mono text-white">{r.code}</td>
                      <td className="py-3 px-5 text-zinc-400 font-mono text-xs">{r.referrer_device}</td>
                      <td className="py-3 px-5 text-right text-zinc-300">{r.visits}</td>
                      <td className="py-3 px-5 text-right text-zinc-300">{r.unique_referred}</td>
                      <td className="py-3 px-5 text-right text-[#ccff00] font-semibold">{r.qualified_count}</td>
                      <td className="py-3 px-5 text-xs text-zinc-500">{r.first_referral_at ? fmt(r.first_referral_at) : "—"}</td>
                      <td className="py-3 px-5 text-xs text-zinc-500">{r.last_referral_at ? fmt(r.last_referral_at) : "—"}</td>
                      <td className="py-3 px-5">
                        {r.indicators && r.indicators.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {r.indicators.map((k) => (
                              <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-200">
                                {k}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-white/5">
              {items.map((r) => (
                <div key={r.code} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-white">{r.code}</div>
                    <div className="text-[#ccff00] font-semibold text-sm">{r.qualified_count} qualified</div>
                  </div>
                  <div className="mt-1 font-mono text-xs text-zinc-500">{r.referrer_device}</div>
                  <div className="mt-2 flex gap-4 text-xs text-zinc-400">
                    <span><Eye className="w-3 h-3 inline mr-1" />{r.visits} visits</span>
                    <span><MapPin className="w-3 h-3 inline mr-1" />{r.unique_referred} referred</span>
                  </div>
                  {r.indicators && r.indicators.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.indicators.map((k) => (
                        <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-200">
                          {k}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <p className="text-xs text-zinc-600">
        Referrer device IDs are masked to their last 6 characters. Full IDs are never exposed.
      </p>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="p-4 rounded-2xl bg-[#141416] border border-white/10">
      <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-[0.22em]">
        {icon} {label}
      </div>
      <div className="mt-2 font-heading text-2xl md:text-3xl font-bold text-white">{value}</div>
    </div>
  );
}

/* ===== Campaign tab ===== */
const DEFAULT_CAMPAIGN_FORM = {
  active: false,
  name: "€100 Fuel Giveaway",
  prize: "€50 fuel voucher",
  prize_pool: "€100",
  total_winners: 2,
  start_date: "",
  end_date: "",
  qualifying_actions: ["drop", "claim"],
  min_qualified_referrals: 1,
  winner_method: "TBC",
  terms_url: "/giveaway-terms",
  eligibility: "TBC",
};

export function CampaignTab({ adminKey }) {
  const [form, setForm] = React.useState(DEFAULT_CAMPAIGN_FORM);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/campaign`, {
        headers: { "X-Admin-Key": adminKey },
      });
      const d = res.data || {};
      setForm({
        active: !!d.active,
        name: d.name || DEFAULT_CAMPAIGN_FORM.name,
        prize: d.prize || DEFAULT_CAMPAIGN_FORM.prize,
        prize_pool: d.prize_pool || DEFAULT_CAMPAIGN_FORM.prize_pool,
        total_winners: Number(d.total_winners || DEFAULT_CAMPAIGN_FORM.total_winners),
        start_date: d.start_date || "",
        end_date: d.end_date || "",
        qualifying_actions: Array.isArray(d.qualifying_actions) && d.qualifying_actions.length ? d.qualifying_actions : ["drop", "claim"],
        min_qualified_referrals: Number(d.min_qualified_referrals || 1),
        winner_method: d.winner_method || "TBC",
        terms_url: d.terms_url || "",
        eligibility: d.eligibility || "TBC",
      });
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Session expired.");
      else toast.error("Failed to load campaign config.");
    } finally {
      setLoading(false);
    }
  }, [adminKey]);
  React.useEffect(() => { load(); }, [load]);

  const toggleAction = (a) => {
    setForm((f) => {
      const set = new Set(f.qualifying_actions);
      if (set.has(a)) set.delete(a); else set.add(a);
      // Guard: keep at least one action.
      const next = set.size === 0 ? ["drop"] : Array.from(set);
      return { ...f, qualifying_actions: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/campaign`, form, {
        headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" },
      });
      toast.success("Campaign settings saved.");
    } catch (err) {
      toast.error("Failed to save campaign settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-zinc-500 rounded-2xl bg-[#141416] border border-white/10">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaign-tab">
      <div className={`p-5 rounded-2xl border ${form.active ? "bg-[#ccff00]/10 border-[#ccff00]/40" : "bg-amber-500/10 border-amber-500/40"}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: form.active ? "#ccff00" : "#fbbf24" }}>
              <Gift className="w-3 h-3 inline mr-1" />
              Campaign status
            </div>
            <div className={`mt-1 font-heading text-xl font-semibold ${form.active ? "text-[#ccff00]" : "text-amber-200"}`}>
              {form.active ? "Active" : "Inactive — hidden from public"}
            </div>
            {!form.active && (
              <div className="mt-2 text-xs text-amber-200/80 leading-relaxed max-w-xl">
                Prize wording is completely hidden on the landing page. Users see only
                the neutral &ldquo;Invite friends&rdquo; CTA. Toggle on once terms and dates are
                finalised.
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-5 h-5 accent-[#ccff00]"
              data-testid="campaign-active-toggle"
            />
            <span className="text-sm">Active</span>
          </label>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Prize name">
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-name"
          />
        </Field>
        <Field label="Prize description">
          <Input
            value={form.prize}
            onChange={(e) => setForm({ ...form, prize: e.target.value })}
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-prize"
          />
        </Field>
        <Field label="Prize pool (public headline)">
          <Input
            value={form.prize_pool}
            onChange={(e) => setForm({ ...form, prize_pool: e.target.value })}
            placeholder="€100"
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-prize-pool"
          />
        </Field>
        <Field label="Total winners">
          <Input
            type="number"
            min={1}
            value={form.total_winners}
            onChange={(e) => setForm({ ...form, total_winners: Math.max(1, Number(e.target.value || 1)) })}
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-total-winners"
          />
        </Field>
        <Field label="Start date (ISO)" hint="Placeholder: To be confirmed">
          <Input
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            placeholder="2026-03-01T00:00:00Z"
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-start"
          />
        </Field>
        <Field label="End date (ISO)" hint="Placeholder: To be confirmed">
          <Input
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            placeholder="2026-03-31T23:59:59Z"
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-end"
          />
        </Field>
        <Field label="Minimum qualified referrals">
          <Input
            type="number"
            min={1}
            value={form.min_qualified_referrals}
            onChange={(e) => setForm({ ...form, min_qualified_referrals: Math.max(1, Number(e.target.value || 1)) })}
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-min"
          />
        </Field>
        <Field label="Winner selection method">
          <Input
            value={form.winner_method}
            onChange={(e) => setForm({ ...form, winner_method: e.target.value })}
            placeholder="e.g. Random draw among qualified referrers"
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-winner"
          />
        </Field>
        <Field label="Terms URL (public)">
          <Input
            value={form.terms_url}
            onChange={(e) => setForm({ ...form, terms_url: e.target.value })}
            placeholder="https://…"
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-terms"
          />
        </Field>
        <Field label="Eligibility">
          <Input
            value={form.eligibility}
            onChange={(e) => setForm({ ...form, eligibility: e.target.value })}
            className="h-11 bg-[#0a0a0c] border-white/10 text-white"
            data-testid="campaign-eligibility"
          />
        </Field>
      </div>

      <Field label="Qualifying actions" hint="A referred device qualifies after any one of these completes server-side.">
        <div className="flex flex-wrap gap-2">
          {[
            { k: "drop", label: "Drop a spot" },
            { k: "claim", label: "Claim a spot" },
          ].map((a) => {
            const on = form.qualifying_actions.includes(a.k);
            return (
              <button
                key={a.k}
                type="button"
                onClick={() => toggleAction(a.k)}
                className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                  on
                    ? "bg-[#ccff00]/15 border-[#ccff00]/40 text-[#ccff00]"
                    : "bg-white/[0.03] border-white/10 text-zinc-400 hover:text-white"
                }`}
                data-testid={`campaign-action-${a.k}`}
              >
                {on && <Check className="w-3 h-3 inline mr-1" />}
                {a.label}
              </button>
            );
          })}
        </div>
      </Field>

      <div className="flex items-center gap-3">
        <Button
          onClick={save}
          disabled={saving}
          className="h-11 bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold rounded-xl px-5"
          data-testid="campaign-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save campaign settings
        </Button>
        <span className="text-xs text-zinc-500">
          Winner selection is <strong className="text-zinc-300">not implemented</strong> yet.
        </span>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-zinc-600">{hint}</div>}
    </div>
  );
}

/* ===== Giveaway participants tab ===== */
export function ParticipantsTab({ adminKey }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/campaign/participants`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setItems(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Session expired.");
      else toast.error("Failed to load participants.");
    } finally {
      setLoading(false);
    }
  }, [adminKey]);
  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (idx, device_id, status) => {
    // We only have the masked ID in the JSON — call the CSV first to get the raw ID.
    // Simpler: keep the admin change flow via masked ID would require server-side lookup.
    // For now, fetch fresh raw IDs from CSV, find match, then PUT.
    setSaving((s) => ({ ...s, [idx]: true }));
    try {
      // The GET /participants intentionally strips raw device_id. We fetch CSV
      // (admin-only) to resolve masked → raw, then update.
      const csvRes = await axios.get(`${API}/admin/campaign/participants.csv`, {
        headers: { "X-Admin-Key": adminKey },
        responseType: "text",
      });
      const rawIds = csvRes.data.split("\n").slice(1).map((row) => row.split(",")[0]);
      const raw = rawIds.find((r) => r && r.endsWith(items[idx].device_id_masked.replace("…", "")));
      if (!raw) throw new Error("Participant not found");
      await axios.put(`${API}/admin/campaign/participants/${encodeURIComponent(raw)}/status`, { status }, {
        headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" },
      });
      toast.success(`Status updated: ${status}`);
      await load();
    } catch (err) {
      toast.error("Failed to update status.");
    } finally {
      setSaving((s) => ({ ...s, [idx]: false }));
    }
  };

  const csvHref = `${API}/admin/campaign/participants.csv`;
  const downloadCSV = async () => {
    try {
      const res = await axios.get(csvHref, { headers: { "X-Admin-Key": adminKey }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `spotdrop-giveaway-participants-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download CSV.");
    }
  };

  const totals = React.useMemo(() => items.reduce((a, it) => ({
    people: a.people + 1,
    entries: a.entries + (it.total_entries || 0),
    active: a.active + (it.status === "active" ? 1 : 0),
    suspicious: a.suspicious + (it.status === "suspicious" ? 1 : 0),
    disq: a.disq + (it.status === "disqualified" ? 1 : 0),
  }), { people: 0, entries: 0, active: 0, suspicious: 0, disq: 0 }), [items]);

  return (
    <div className="space-y-4" data-testid="participants-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Participants" value={totals.people} icon={<Users className="w-3.5 h-3.5" />} />
        <StatCard label="Total entries" value={totals.entries} icon={<Gift className="w-3.5 h-3.5" />} />
        <StatCard label="Suspicious" value={totals.suspicious} icon={<AlertTriangle className="w-3.5 h-3.5" />} />
        <StatCard label="Disqualified" value={totals.disq} icon={<Ban className="w-3.5 h-3.5" />} />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">Total: <span className="text-white">{items.length}</span></div>
        <div className="flex items-center gap-2">
          <Button onClick={downloadCSV} variant="outline" size="sm" className="h-8 border-white/10 text-zinc-300 hover:bg-white/5" data-testid="participants-csv">
            <Download className="w-3.5 h-3.5 mr-1.5" />
            CSV
          </Button>
          <Button onClick={load} variant="ghost" size="sm" className="h-8 text-zinc-300 hover:bg-white/5" data-testid="participants-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-zinc-500" data-testid="participants-empty">
            No giveaway participants yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.18em] text-zinc-500 border-b border-white/5">
                  <th className="py-3 px-4 font-semibold">Device</th>
                  <th className="py-3 px-4 font-semibold">Contact</th>
                  <th className="py-3 px-4 font-semibold text-right">Entries</th>
                  <th className="py-3 px-4 font-semibold text-right">Save</th>
                  <th className="py-3 px-4 font-semibold text-right">Drop/Claim</th>
                  <th className="py-3 px-4 font-semibold text-right">Referrals</th>
                  <th className="py-3 px-4 font-semibold text-right">Install</th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody data-testid="participants-table">
                {items.map((p, idx) => {
                  const b = p.breakdown || {};
                  const dropClaim = (b.drop?.entries || 0) + (b.claim?.entries || 0);
                  return (
                    <tr key={p.device_id_masked + idx} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-3 px-4 font-mono text-xs text-zinc-400" data-testid={`participants-row-device-${idx}`}>{p.device_id_masked}</td>
                      <td className="py-3 px-4 text-white">
                        {p.email && <div className="truncate max-w-[200px]">{p.email}</div>}
                        {p.instagram && <div className="text-xs text-zinc-500">@{p.instagram}</div>}
                      </td>
                      <td className="py-3 px-4 text-right text-[#ccff00] font-semibold">{p.total_entries}</td>
                      <td className="py-3 px-4 text-right text-zinc-300">{b.save_car?.entries || 0}</td>
                      <td className="py-3 px-4 text-right text-zinc-300">{dropClaim}</td>
                      <td className="py-3 px-4 text-right text-zinc-300">{b.referral_qualified?.entries || 0}</td>
                      <td className="py-3 px-4 text-right text-zinc-300">{b.install?.entries || 0}</td>
                      <td className="py-3 px-4">
                        <select
                          value={p.status}
                          disabled={saving[idx]}
                          onChange={(e) => updateStatus(idx, p.device_id_masked, e.target.value)}
                          className={`h-8 rounded px-2 text-xs border ${
                            p.status === "active" ? "bg-[#ccff00]/10 border-[#ccff00]/40 text-[#ccff00]"
                            : p.status === "suspicious" ? "bg-amber-500/10 border-amber-500/40 text-amber-200"
                            : "bg-red-500/10 border-red-500/40 text-red-200"
                          }`}
                          data-testid={`participants-status-${idx}`}
                        >
                          <option value="active">active</option>
                          <option value="suspicious">suspicious</option>
                          <option value="disqualified">disqualified</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-zinc-600">
        Full device IDs are only exposed via CSV export for record-keeping.
      </p>
    </div>
  );
}


/* ===== Notifications tab (daily reminder) ===== */
export function NotificationsTab({ adminKey }) {
  const [cfg, setCfg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/push`, { headers: { "X-Admin-Key": adminKey } });
      setCfg(res.data);
    } catch {
      toast.error("Failed to load notification settings.");
    } finally {
      setLoading(false);
    }
  }, [adminKey]);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/push`,
        { daily_reminder_enabled: !!cfg.daily_reminder_enabled, hour: Number(cfg.hour), minute: Number(cfg.minute) },
        { headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" } }
      );
      setCfg(res.data);
      toast.success("Notification settings saved.");
    } catch {
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(
        `${API}/admin/push/test`,
        { title: "SpotDrop test", body: "Test push — notifications are working." },
        { headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" } }
      );
      toast.success(`Test sent to ${res.data.sent} subscriber(s).`);
      load();
    } catch {
      toast.error("Test push failed.");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="p-10 flex items-center justify-center text-zinc-500 rounded-2xl bg-[#141416] border border-white/10">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-notifications-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Subscribers" value={cfg.subscribers} icon={<Bell className="w-3.5 h-3.5" />} />
        <StatCard label="Devices" value={cfg.devices} icon={<Users className="w-3.5 h-3.5" />} />
        <StatCard label="Last send" value={cfg.last_sent_count} icon={<Check className="w-3.5 h-3.5" />} />
        <StatCard label="Push configured" value={cfg.push_enabled ? "Yes" : "No"} icon={<ShieldCheck className="w-3.5 h-3.5" />} />
      </div>

      <div className="p-5 rounded-2xl bg-[#141416] border border-white/10 space-y-4">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!cfg.daily_reminder_enabled}
            onChange={(e) => setCfg({ ...cfg, daily_reminder_enabled: e.target.checked })}
            className="w-5 h-5 accent-[#ccff00]"
            data-testid="push-reminder-toggle"
          />
          <span className="text-sm">Daily reminder enabled</span>
        </label>

        <div className="grid grid-cols-2 gap-3 max-w-xs">
          <Field label="Hour (Malta)">
            <Input
              type="number"
              min={0}
              max={23}
              value={cfg.hour}
              onChange={(e) => setCfg({ ...cfg, hour: e.target.value })}
              className="h-11 bg-[#0a0a0c] border-white/10 text-white"
              data-testid="push-hour"
            />
          </Field>
          <Field label="Minute">
            <Input
              type="number"
              min={0}
              max={59}
              value={cfg.minute}
              onChange={(e) => setCfg({ ...cfg, minute: e.target.value })}
              className="h-11 bg-[#0a0a0c] border-white/10 text-white"
              data-testid="push-minute"
            />
          </Field>
        </div>

        <div className="text-[12px] text-zinc-400">
          Message: <span className="text-zinc-200">{cfg.body}</span>
        </div>
        <div className="text-[11px] text-zinc-600">
          Last sent: {cfg.last_sent_at ? fmt(cfg.last_sent_at) : "never"} · sent once per day, Malta time.
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={save}
            disabled={saving}
            className="h-11 bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold rounded-xl px-5"
            data-testid="push-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          <Button
            onClick={sendTest}
            disabled={testing || !cfg.subscribers}
            variant="outline"
            className="h-11 border-white/10 text-zinc-200 hover:bg-white/5 rounded-xl px-5"
            data-testid="push-test"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
            Send test push
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Activation funnel ---------- */
const RANGES = [
  { k: "7", label: "7 days" },
  { k: "30", label: "30 days" },
  { k: "all", label: "All time" },
];

export function FunnelTab({ adminKey }) {
  const [range, setRange] = React.useState("7");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/activation/funnel`, {
        headers: { "X-Admin-Key": adminKey },
        params: { range },
      });
      setData(res.data);
    } catch {
      toast.error("Could not load the funnel.");
    } finally {
      setLoading(false);
    }
  }, [adminKey, range]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-funnel-tab">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex gap-1" data-testid="funnel-range">
          {RANGES.map((r) => (
            <button
              key={r.k}
              type="button"
              onClick={() => setRange(r.k)}
              className={`h-8 px-3 rounded-full text-[11px] font-medium ${
                range === r.k ? "bg-[#ccff00] text-[#0a0a0c]" : "bg-white/5 text-zinc-400"
              }`}
              data-testid={`funnel-range-${r.k}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Button
          type="button"
          onClick={load}
          className="h-8 px-3 bg-white/5 border border-white/10 text-xs"
          data-testid="funnel-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading && !data ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#ccff00]" />
        </div>
      ) : (
        <>
          <div className="text-[11px] text-zinc-500 mb-2" data-testid="funnel-devices">
            {data?.devices ?? 0} unique devices in this period
          </div>
          <div className="space-y-1.5">
            {(data?.steps || []).map((s, i) => (
              <div
                key={s.key}
                className="p-3 rounded-xl bg-white/[0.03] border border-white/10"
                data-testid={`funnel-step-${s.key}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[13px] text-white">{s.label}</div>
                  <div className="text-[13px] font-mono text-[#ccff00]">{s.users}</div>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#ccff00]/70" style={{ width: `${s.pct_of_total}%` }} />
                </div>
                <div className="mt-1 text-[10px] text-zinc-500">
                  {s.pct_of_total}% of all devices
                  {i > 0 ? ` · ${s.pct_of_previous}% of previous step` : ""}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ===== Spot history tab (lifecycle stats, survives TTL purge) ===== */
const HISTORY_RANGES = [
  { k: "1", label: "24 hours" },
  { k: "7", label: "7 days" },
  { k: "30", label: "30 days" },
  { k: "all", label: "All time" },
];

const EVENT_LABEL = {
  created: "Created",
  claimed: "Claimed",
  expired: "Expired",
  cancelled: "Cancelled",
  handover_confirmed: "Handover confirmed",
};

const EVENT_COLOR = {
  created: "text-[#ccff00]",
  claimed: "text-[#67e8f9]",
  expired: "text-zinc-400",
  cancelled: "text-amber-300",
  handover_confirmed: "text-emerald-300",
};

export function HistoryTab({ adminKey }) {
  const [range, setRange] = React.useState("7");
  const [stats, setStats] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": adminKey };
      const [s, e] = await Promise.all([
        axios.get(`${API}/admin/spot-stats`, { headers, params: { range } }),
        axios.get(`${API}/admin/spot-history`, { headers, params: { range, limit: 200 } }),
      ]);
      setStats(s.data);
      setEvents(e.data?.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load spot history.");
    } finally {
      setLoading(false);
    }
  }, [adminKey, range]);

  React.useEffect(() => { load(); }, [load]);

  const t = stats?.totals || {};
  const maxHour = Math.max(1, ...((stats?.by_hour || []).map((h) => h.total)));

  return (
    <div data-testid="admin-history-tab">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex gap-1" data-testid="history-range">
          {HISTORY_RANGES.map((r) => (
            <button
              key={r.k}
              type="button"
              onClick={() => setRange(r.k)}
              className={`h-8 px-3 rounded-full text-[11px] font-medium ${
                range === r.k ? "bg-[#ccff00] text-[#0a0a0c]" : "bg-white/5 text-zinc-400"
              }`}
              data-testid={`history-range-${r.k}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Button onClick={load} size="sm" variant="ghost" className="h-8 text-zinc-300" data-testid="history-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="history-stats">
        <StatCard label="Created" value={t.created ?? 0} icon={<MapPin className="w-3.5 h-3.5" />} />
        <StatCard label="Claimed" value={t.claimed ?? 0} icon={<Check className="w-3.5 h-3.5" />} />
        <StatCard label="Expired" value={t.expired ?? 0} icon={<Clock className="w-3.5 h-3.5" />} />
        <StatCard label="Cancelled" value={t.cancelled ?? 0} icon={<Trash2 className="w-3.5 h-3.5" />} />
        <StatCard label="Devices" value={stats?.unique_devices ?? 0} icon={<Users className="w-3.5 h-3.5" />} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className="rounded-2xl bg-[#141416] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-3">
            By date (Malta)
          </div>
          {(stats?.by_day || []).length === 0 ? (
            <div className="text-xs text-zinc-600 py-6 text-center" data-testid="history-byday-empty">No activity yet.</div>
          ) : (
            <div className="space-y-2" data-testid="history-byday">
              {stats.by_day.slice(0, 14).map((d) => (
                <div key={d.day} className="flex items-center justify-between gap-3 text-[12px]">
                  <span className="text-zinc-300 font-mono">{d.day}</span>
                  <span className="flex gap-2">
                    {d.events.map((e) => (
                      <span key={e.event} className={EVENT_COLOR[e.event] || "text-zinc-400"}>
                        {EVENT_LABEL[e.event] || e.event}: {e.count}
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-[#141416] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-3">
            By hour of day (Malta)
          </div>
          {(stats?.by_hour || []).length === 0 ? (
            <div className="text-xs text-zinc-600 py-6 text-center">No activity yet.</div>
          ) : (
            <div className="space-y-1.5" data-testid="history-byhour">
              {stats.by_hour.map((h) => (
                <div key={h.hour} className="flex items-center gap-2">
                  <span className="w-10 text-[11px] font-mono text-zinc-500">
                    {String(h.hour).padStart(2, "0")}:00
                  </span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-[#ccff00]/70" style={{ width: `${(100 * h.total) / maxHour}%` }} />
                  </div>
                  <span className="w-8 text-right text-[11px] font-mono text-zinc-400">{h.total}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl bg-[#141416] border border-white/10 p-4 mt-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-3">
          By general area
        </div>
        {(stats?.by_area || []).length === 0 ? (
          <div className="text-xs text-zinc-600 py-6 text-center">No activity yet.</div>
        ) : (
          <div className="space-y-2" data-testid="history-byarea">
            {stats.by_area.slice(0, 20).map((a) => (
              <div key={`${a.area}-${a.locality}`} className="flex items-center justify-between gap-3 text-[12px]">
                <span className="text-zinc-300">
                  {a.locality || "Unknown area"}
                  <span className="ml-2 text-[10px] font-mono text-zinc-600">{a.area || "—"}</span>
                </span>
                <span className="flex gap-3 text-zinc-400">
                  <span className="text-[#ccff00]">{a.created} created</span>
                  <span className="text-[#67e8f9]">{a.claimed} claimed</span>
                  <span>{a.devices} dev</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden mt-4">
        <div className="px-4 pt-4 text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
          <History className="w-3 h-3 inline mr-1" /> Recent events
        </div>
        {events.length === 0 ? (
          <div className="p-10 text-center text-zinc-500" data-testid="history-events-empty">
            No lifecycle events recorded for this period.
          </div>
        ) : (
          <div className="divide-y divide-white/5 mt-3" data-testid="history-events">
            {events.map((e) => (
              <div key={e.id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
                <span className={`font-semibold ${EVENT_COLOR[e.event] || "text-zinc-300"}`}>
                  {EVENT_LABEL[e.event] || e.event}
                </span>
                <span className="text-zinc-500">{e.locality || e.area || "—"}</span>
                {e.duration_min ? <span className="text-zinc-600">{e.duration_min} min</span> : null}
                <span className="text-zinc-600">{e.source}</span>
                <span className="text-zinc-600 font-mono text-[10px]">dev {e.device_hash ? e.device_hash.slice(0, 8) : "—"}</span>
                <span className="ml-auto text-zinc-500">{fmt(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-zinc-600 mt-3">
        Anonymous history: hashed device ids and ~110 m area cells only. Retained 400 days.
        Expired drops never return to the live map. All times Malta (Europe/Malta).
      </p>
    </div>
  );
}
